import { Box, Button, Typography } from '@oresundsbron/bridge-ui';
import {
  PrivateContactInformation,
  BusinessContactInformation as BusinessContactInfo,
  PrivateCustomer,
  SignupThirdParty,
  SignupResponse,
  thirdPartyPaymentEntryParser,
  constants,
  lastNameOptionalFormat,
} from '@oresundsbron/validators';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ContactInformation from './ContactInformation';
import Recognitions from './Recognitions';
import { ThirdPartySignup, Recognition } from './types';
import { isInvalid, sortRecognitions } from './utils';
import NewsSubscription from './NewsSubscription';
import TermsOfAgreement from './TermsOfAgreement';
import { useForm, ValidationResult } from '@oresundsbron/use-form';
import { isBackendError } from '../../../lib/errors';
import { pipe } from 'fp-ts/lib/function';
import { useTypeSafeMutation } from '@oresundsbron/api';
import { fromPredicate, match as matchO } from 'fp-ts/lib/Option';
import SignupErrors from './SignupErrors';
import BusinessContactInformation, {
  Labels,
} from './BusinessContactInformation';

import { validateT } from '../../../lib/zod';
import { fromTask, getOrElseW, map as mapT } from 'fp-ts/lib/TaskOption';
import { of } from 'fp-ts/lib/Task';
import { useBannerVisibility } from '@oresundsbron/app-common';
import { SignupFormDataPersonalDetails } from '../brobizz';
import { signUpStoreActions } from '@oresundsbron/app-common';

const { setError, setResponse, setToken } = signUpStoreActions;

interface SignupFormProps {
  data: SignupFormDataPersonalDetails;
  token: string;
  business: boolean;
}

const onError = (e: unknown) =>
  pipe(
    e,
    fromPredicate(isBackendError),
    matchO(
      () => {},
      (e) => setError(e)
    )
  );

const SignUpForm: FC<SignupFormProps> = ({ data, token, business }) => {
  const namespace = business ? 'businessSignUp' : 'privateSignUp';
  const { setBannerVisible } = useBannerVisibility();
  const [skipValidation, setSkipValidation] = useState(false);

  const { t } = useTranslation([namespace, 'common'], {
    nsMode: 'fallback',
  });

  const [contactInfoValidation, setContactInfoValidation] = useState<
    ValidationResult<string> | undefined
  >();

  /**
   * [BBAS 3308] temporary option to hide contact number for brobizz signup
   * Phone number will added back with [BBAS 3309]
   */
  const isHidePhone = true;

  const { mutateAsync, isLoading } = useTypeSafeMutation(
    ['signup', 'skyttelpass'],
    ({ Signup }) => Signup.skyttel,
    {
      onMutate: () => {
        setBannerVisible(false);
      },
      onSuccess: (data) => {
        setResponse(data as SignupResponse);

        setToken(data.code);
      },
      onError,
    }
  );

  const hasContactValidationErrors = useMemo(
    () =>
      pipe(
        data.contractType === constants.OresundBusiness
          ? data.contactInformation.contact
          : data.contactInformation,
        validateT(lastNameOptionalFormat.spa, t),
        fromTask,
        mapT(setContactInfoValidation),
        getOrElseW(() => of(undefined))
      ),
    [data, t]
  );

  useEffect(() => {
    hasContactValidationErrors();
  }, [hasContactValidationErrors]);

  const isNotValidContactName = useMemo(
    () =>
      !!contactInfoValidation?.firstName || !!contactInfoValidation?.lastName,
    [contactInfoValidation]
  );

  const hasErrors = useMemo(
    () => (data.errors && data.errors.length > 0) || isNotValidContactName,
    [data.errors, isNotValidContactName]
  );

  const dataErrors = useMemo(
    () =>
      isNotValidContactName
        ? [
            ...data.errors,
            { message: constants.CONTACT_NAME_LENGTH_MAX_EXCEEDED.value },
          ]
        : data.errors,
    [data.errors, isNotValidContactName]
  );

  const onSubmit = useCallback(
    async (inc: ThirdPartySignup) => {
      const paymentEntries = inc.payment.paymentEntries.filter(
        ({ country, licensePlate }) =>
          licensePlate !== '' ? country !== '' : true
      );

      const body = {
        ...inc,
        contactInformation: {
          ...data.contactInformation,
        },
        paymentAgreement: {
          ...data.paymentAgreement,
          oresundsPay: false,
        },
        payment: {
          paymentEntries: paymentEntries.map(
            ({ weightClass = false, hybrid = false, ...r }) => ({
              ...r,
              weightClass,
              hybrid,
              isNew: false,
            })
          ),
        },
        termsOfAgreement: {
          currency: 'DKK',
          agreement: 'true',
        },
        token,
      } as SignupThirdParty;

      await mutateAsync(body as SignupThirdParty);
    },
    [data, token, mutateAsync]
  );

  const { handleSubmit } = useForm<ThirdPartySignup>({
    namespace,
    defaultValues: {
      ...data,
      paymentAgreement: {
        ...data.paymentAgreement,
        oresundsPay: false,
      },
      paymentMethod: undefined,
      subscribeToClub: {
        subscribe: undefined,
      },
      payment: {
        paymentEntries: (
          data.payment.paymentEntries.map((entry) => ({
            ...entry,
            invalid: isInvalid(entry, business),
          })) as Recognition[]
        ).sort(sortRecognitions),
      },
    },
    onSubmit,
  });

  const privateLabels = useMemo(
    (): PrivateContactInformation => ({
      firstName: t('form.labels.firstName'),
      lastName: t('form.labels.lastName'),
      country: t('form.labels.country'),
      zipCode: t('form.labels.zipCode'),
      city: t('form.labels.city'),
      address: t('form.labels.address'),
      phoneCode: t('form.labels.phoneCode'),
      phone: t('form.labels.phone'),
      email: t('form.labels.email'),
    }),
    [t]
  );

  const businessLabels = useMemo(
    (): Labels => ({
      contact: {
        firstName: t('form.labels.firstName'),
        lastName: t('form.labels.lastName'),
        email: t('form.labels.email'),
        phoneCode: t('form.labels.phoneCode'),
        phone: t('form.labels.phone'),
      },
      companyName: t('form.labels.companyName'),
      country: t('form.labels.country'),
      zipCode: t('form.labels.zipCode'),
      city: t('form.labels.city'),
      address: t('form.labels.address'),
      ean: t('form.labels.ean'),
      contactPerson: t('form.labels.contactPerson', {
        ns: 'common',
      }),
      vatno: t('form.labels.vatno'),
      phoneCode: t('form.labels.phoneCode'),
      phone: t('form.labels.phone'),
    }),
    [t]
  );

  return (
    <>
      <Typography intent="label" className="col-span-full">
        {t('form.requiredFields')}
      </Typography>
      <SignupErrors errors={dataErrors} />
      <Box
        as="form"
        className="col-span-full flex flex-col gap-8 md:col-span-8 md:gap-16"
        onSubmit={handleSubmit}
      >
        {business && (
          <BusinessContactInformation
            title={t('form.sections.contactInformation.title')}
            disclaimer={t(
              'form.sections.contactInformation.skyttelpassDisclaimer'
            )}
            data={data.contactInformation as BusinessContactInfo}
            labels={businessLabels}
            isHidePhone={isHidePhone}
            contactInfoValidation={contactInfoValidation}
          />
        )}
        {!business && (
          <ContactInformation
            title={t('form.sections.contactInformation.title')}
            disclaimer={t(
              'form.sections.contactInformation.skyttelpassDisclaimer'
            )}
            data={data.contactInformation as PrivateCustomer}
            labels={privateLabels}
            isHidePhone={isHidePhone}
            contactInfoValidation={contactInfoValidation}
          />
        )}
        <Recognitions
          namespace={namespace}
          onValidate={thirdPartyPaymentEntryParser.array().spa}
          isBusiness={business}
          disabled={false}
          skipValidation={skipValidation}
        />
        <Box className="flex flex-col items-end">
          <Button
            className="mb-4"
            onClick={() => setSkipValidation(!skipValidation)}
          >
            {skipValidation
              ? t('form.sections.recognitions.dontSkipValidationLabel')
              : t('form.sections.recognitions.skipValidationLabel')}
          </Button>
          <Typography size="sm" className="text-right">
            {t('form.sections.recognitions.skipValidationDisclaimer')}
          </Typography>
        </Box>
        <NewsSubscription namespace={namespace} disabled={hasErrors} />
        <TermsOfAgreement
          namespace={namespace}
          isLoading={isLoading}
          disabled={hasErrors}
          section={business ? 'business' : 'private'}
          contractType={
            business ? constants.OresundBusiness : constants.OresundGo
          }
        />
      </Box>
    </>
  );
};

export default SignUpForm;
